import Dot from 'components/Dot/Dot';

import {
  RETAIL_RATE_REQUEST_STATUS as STATUS,
  RETAIL_RATE_REQUEST_STATUS_REASON as STATUS_REASON,
  getRetailRateRequestStatus,
  getRetailRateRequestStatusReason,
  isRetailRateRequestActionRequired,
} from '../ServicePricingSection/pricingStatusStepper.helpers';

import { messages } from './PricingStatusStepper.messages';

// Visual state of a single timeline step.
export const STEP_STATE = {
  DONE: 'done',
  CURRENT: 'current',
  FAILED: 'failed',
  UPCOMING: 'upcoming',
};

// Rejections only ever carry these four reasons. RATE_CHANGES_NEEDED and
// PROOF_CHANGES_NEEDED are approval-only (Figma "States" board + admin approval
// rules), so a real rejection can't reach the reasonGeneric fallback below —
// keep it only as a defensive default, and don't add the approval reasons here.
const REJECTION_REASON_MESSAGE = {
  [STATUS_REASON.PROOF_UNTRUSTED]: messages.reasonProofUntrusted,
  [STATUS_REASON.PARTNER_DECLINED]: messages.reasonPartnerDeclined,
  [STATUS_REASON.GENRE_DISALLOWED]: messages.reasonGenreDisallowed,
  [STATUS_REASON.PARTNER_DISAGREEMENT]: messages.reasonPartnerDisagreement,
};

const rejectionReasonMessage = statusReason => REJECTION_REASON_MESSAGE[statusReason] || messages.reasonGeneric;

const isApproved = retailRateRequest => getRetailRateRequestStatus(retailRateRequest) === STATUS.APPROVED;

// PENDING, REQUESTED and MANUAL_HANDOFF all mean "submitted, awaiting
// verification" — handoff is just review happening outside the automated flow.
// Matching the list-page helper keeps the badge and the edit page in agreement
// and prevents a second submission while the first is being handled.
const isPending = retailRateRequest => {
  const status = getRetailRateRequestStatus(retailRateRequest);
  return status === STATUS.PENDING || status === STATUS.REQUESTED || status === STATUS.MANUAL_HANDOFF;
};

const submittedStep = { titleMessage: messages.stepSubmittedTitle, state: STEP_STATE.DONE };

const failedView = reasonMessage => ({
  isRejected: true,
  statusLabel: messages.statusVerificationFailed,
  statusColor: Dot.COLORS.RED,
  steps: [
    submittedStep,
    { titleMessage: messages.stepVerificationTitle, state: STEP_STATE.DONE },
    { titleMessage: messages.stepFailedTitle, subtitleMessage: reasonMessage, state: STEP_STATE.FAILED },
  ],
});

// Maps a RetailRateRequest to the view model the stepper renders: the three
// timeline steps plus a compact status label/colour. Returns null when there's
// no request to display (caller renders nothing).
export const getPricingStatusView = retailRateRequest => {
  if (!getRetailRateRequestStatus(retailRateRequest)) return null;

  if (isRetailRateRequestActionRequired(retailRateRequest)) {
    return failedView(rejectionReasonMessage(getRetailRateRequestStatusReason(retailRateRequest)));
  }

  if (isApproved(retailRateRequest)) {
    const rateChange = getRetailRateRequestStatusReason(retailRateRequest) === STATUS_REASON.RATE_CHANGES_NEEDED;
    return {
      isRejected: false,
      statusLabel: rateChange ? messages.statusAvailableWithChanges : messages.statusAvailable,
      statusColor: Dot.COLORS.GREEN,
      steps: [
        submittedStep,
        { titleMessage: messages.stepVerificationTitle, state: STEP_STATE.DONE },
        {
          titleMessage: messages.stepApprovedTitle,
          subtitleMessage: rateChange ? messages.stepApprovedRateChangeSubtitle : messages.stepApprovedSubtitle,
          state: STEP_STATE.DONE,
        },
      ],
    };
  }

  if (isPending(retailRateRequest)) {
    return {
      isRejected: false,
      statusLabel: messages.statusPendingReview,
      statusColor: Dot.COLORS.YELLOW,
      steps: [
        submittedStep,
        {
          titleMessage: messages.stepVerificationTitle,
          subtitleMessage: messages.stepVerificationSubtitle,
          state: STEP_STATE.CURRENT,
        },
        { titleMessage: messages.stepApprovedTitle, state: STEP_STATE.UPCOMING },
      ],
    };
  }

  // Pricing is read-only for any request, so an unknown status (e.g. one this
  // proto version doesn't know yet) must still explain itself and point to support.
  return failedView(messages.reasonGeneric);
};

// A service only takes one retail rate request: any status, rejected and
// cancelled included, makes pricing read-only. Changes go through support.
export const blocksEditing = retailRateRequest => !!getRetailRateRequestStatus(retailRateRequest);
